import { Injectable } from "@angular/core";
import { LoadingController, PopoverController } from "@ionic/angular";
import { BehaviorSubject, Observable } from "rxjs";

declare const Dropbox: any;

@Injectable({
  providedIn: "root",
})
export class DropboxPickerService {
  pickerCallback;
  response$ = new BehaviorSubject<any>([]);
  loading: HTMLIonLoadingElement;
  private processedFiles = new Set();
  options = {
    // Required. Called when a user selects an item in the Chooser.
    success: (files) => {
      this.pickerResponse(files);
    },

    // Optional. Called when the user closes the dialog without selecting a file
    // and does not include any parameters.
    cancel: () => {
      // Limpiar el estado cuando se cancela
      this.clearState();
    },

    // Optional. "preview" (default) is a preview link to the document for sharing,
    // "direct" is an expiring link to download the contents of the file. For more
    // information about link types, see Link types below.
    linkType: "direct", // or "direct"

    // Optional. A value of false (default) limits selection to a single file, while
    // true enables multiple file selection.
    multiselect: true, // or true

    // Optional. This is a list of file extensions. If specified, the user will
    // only be able to select files with these extensions. You may also specify
    // file types, such as "video" or "images" in the list. For more information,
    // see File types below. By default, all extensions are allowed.
    // extensions: ['.pdf', '.doc', '.docx'],
    extensions: [".pdf"],

    // Optional. A value of false (default) limits selection to files,
    // while true allows the user to select both folders and files.
    // You cannot specify `linkType: "direct"` when using `folderselect: true`.
    folderselect: false, // or true

    // Optional. A limit on the size of each file that may be selected, in bytes.
    // If specified, the user will only be able to select files with size
    // less than or equal to this limit.
    // For the purposes of this option, folders have size zero.
    sizeLimit: 300 * 1024 * 1024, // 300Mb or any positive number
  };

  constructor(
    private loadingController: LoadingController,
    private popoverController: PopoverController
  ) {
    this.createLoader();
  }

  async createLoader() {
    if (this.loading) {
      await this.loading.dismiss().catch(() => { });
    }
    this.loading = await this.loadingController.create({
      cssClass: "my-custom-class",
      message: "Cargando archivos...",
      spinner: "bubbles",
    });
  }

  open() {
    this.clearState();
    Dropbox.choose(this.options);
  }

  getResponse(): Observable<File[]> {
    return this.response$.asObservable();
  }

  async pickerResponse(responseFiles) {
    try {
      if (!responseFiles || responseFiles.length === 0) {
        this.clearState();
        return;
      }

      const newFiles = responseFiles.filter(
        (file) => !this.processedFiles.has(file.link)
      );

      if (newFiles.length === 0) {
        this.clearState();
        return;
      }

      await this.loading.present().catch(() => { });

      newFiles.forEach((file) => this.processedFiles.add(file.link));

      const files = await this.getFiles(newFiles);

      await this.loading.dismiss().catch(() => { });

      this.response$.next(files);

      setTimeout(() => this.clearState(), 1000);
    } catch (error) {
      console.error("Error processing Dropbox files:", error);
      await this.loading.dismiss().catch(() => { });
      this.clearState();
    }
  }

  async getFiles(files: any): Promise<File[]> {
    return new Promise((resolve, reject) => {
      const promises = files.map((file) => {
        return new Promise<File>((fileResolve, fileReject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("GET", file.link, true);
          xhr.responseType = "blob";
          xhr.timeout = 30000;

          xhr.onload = () => {
            if (xhr.status === 200) {
              const blob = xhr.response;
              fileResolve(
                new File([blob], file.name, { type: "application/pdf" })
              );
            } else {
              fileReject(new Error(`Failed to download ${file.name}`));
            }
          };

          xhr.onerror = () =>
            fileReject(new Error(`Network error downloading ${file.name}`));
          xhr.ontimeout = () =>
            fileReject(new Error(`Timeout downloading ${file.name}`));

          xhr.send();
        });
      });

      Promise.all(promises).then(resolve).catch(reject);
    });
  }

  private clearState() {
    this.response$.next([]);
    setTimeout(() => {
      this.processedFiles.clear();
    }, 2000);
  }

  public reset() {
    this.clearState();
  }
}
